import { useCallback, useMemo, useState } from 'react';
import type { SignalRow } from '@shared/flow/signal-view';
import type { FlowTableSelectionConfig } from '../types';
import { partitionDialableSignals, type DialableSelection, type DialableSignalInput } from '../trellus/dialableSignals';

/**
 * Signal Response row selection (Signals V3 PR 2, U10; KTD 8). Component
 * state, not a store (R11): `useState<ReadonlySet<string>>` handed to the
 * ledger as a controlled `FlowTableSelectionConfig` — the ledger renders the
 * checkbox column and resolves Cmd/Ctrl-click (toggle, never expands) and
 * Shift-click (range) itself; this hook only unions/toggles ids.
 *
 * Ids that leave `rows` (an archive's `['flow-signals']` refetch drops the
 * row, a filter change) are pruned DURING RENDER — React's "adjust state when
 * a prop changes" pattern, the same one `useContactTableModel.tsx` uses —
 * never an effect that sets state. Nothing to prune ⇒ no state update, so the
 * Set (and the returned config) keeps its identity across row churn.
 *
 * Trellus: this hook publishes NOTHING. The selection cluster's dial segment is the one publisher per page; it
 * watches `changeSeq`, which moves on every USER change (checkbox, range, select-all, Clear — including the bulk
 * stepper's reset after its final archive, deliberately explicit: that selection really did become empty by the rep's
 * own completed action) and never on the render-time prune above.
 */
export interface SignalSelection<Row extends Pick<SignalRow, 'signal_id'>> extends FlowTableSelectionConfig<Row> {
  /** Empties the selection (Clear, and the stepper's reset after the last archive). A user change: bumps `changeSeq`. */
  clear: () => void;
  /** Selects every row currently in `rows`. */
  selectAllVisible: () => void;
  /** The checked rows Trellus may dial, and why the rest are left out. This is what is published to Trellus. */
  dialable: DialableSelection;
  /** Moves on every user change to the checked set and never on the prune; the cluster publishes when it moves. */
  changeSeq: number;
}

const EMPTY: ReadonlySet<string> = new Set();

function ariaLabelFor(row: Pick<SignalRow, 'signal_id'> & Partial<Pick<SignalRow, 'contact_name'>>): string {
  return row.contact_name ? `Select ${row.contact_name}` : 'Select row';
}

export function useSignalSelection<Row extends DialableSignalInput & Partial<Pick<SignalRow, 'contact_name'>>>(
  rows: readonly Row[],
): SignalSelection<Row> {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(EMPTY);
  const [changeSeq, setChangeSeq] = useState(0);
  const bump = useCallback(() => setChangeSeq(n => n + 1), []);

  const [prevRows, setPrevRows] = useState(rows);
  if (rows !== prevRows) {
    setPrevRows(rows);
    if (selectedIds.size > 0) {
      const loaded = new Set(rows.map(r => r.signal_id));
      const kept = [...selectedIds].filter(id => loaded.has(id));
      if (kept.length !== selectedIds.size) setSelectedIds(new Set(kept));
    }
  }

  const onToggle = useCallback((id: string) => {
    bump();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [bump]);
  const onToggleRange = useCallback((ids: string[]) => {
    bump();
    setSelectedIds(prev => new Set([...prev, ...ids]));
  }, [bump]);
  const onToggleAllLoaded = useCallback((ids: string[]) => {
    bump();
    setSelectedIds(prev => {
      if (ids.length > 0 && ids.every(id => prev.has(id))) {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      }
      return new Set([...prev, ...ids]);
    });
  }, [bump]);
  const clear = useCallback(() => {
    bump();
    setSelectedIds(EMPTY);
  }, [bump]);
  const selectAllVisible = useCallback(() => {
    bump();
    setSelectedIds(new Set(rows.map(r => r.signal_id)));
  }, [rows, bump]);

  const dialable = useMemo(() => partitionDialableSignals(rows, selectedIds), [rows, selectedIds]);

  return useMemo(
    () => ({ selectedIds, onToggle, onToggleRange, onToggleAllLoaded, ariaLabelFor, clear, selectAllVisible, dialable, changeSeq }),
    [selectedIds, onToggle, onToggleRange, onToggleAllLoaded, clear, selectAllVisible, dialable, changeSeq],
  );
}
